"use client"

import React, {useEffect, useState, useRef} from "react"
import {Button} from "@/components/ui/button"
import {Card, CardContent} from "@/components/ui/card"
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip"
import {RotateCcw, Save, FolderOpen, ChevronLeft, ChevronRight, X} from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import LogoHeader from "../header/logo-header"
import {convertUrlToFile} from "@/app/components/functions/util/file-url"
// The filters this module also exports were the OCR preprocessing removed in
// DYS-013. What remains is rotation's canvas and a plain resize.
import { loadImageToCanvas, fitWithinMaxEdge } from "@/app/components/functions/util/image-transformations-for-ocr";
import { logger } from "@/lib/logger";
import { analyzeImageQuality, QualityResult } from "@/app/components/functions/util/image-quality-checker";
import QualityWarningBanner from "./quality-warning-banner";
import { setJobContext } from "@/lib/job-context";
import { saveFileToDevice } from "@/app/components/functions/util/save-file";
import { REENCODE_MIME, REENCODE_JPEG_QUALITY, isWithinUploadLimit, MAX_UPLOAD_BYTES, formatMegabytes } from "@/app/components/functions/util/upload-limits";
import { toast } from "sonner";

interface PictureScreenProps {
    pictureFile: File
    onBackToHome: () => void
    onOCRPicture: (file: File, orientation: number) => Promise<void>
    onOCRCollection?: () => Promise<void>
    onNavigateToHome: () => void
    source: "camera" | "file"
    onRetake?: () => void
    onChangeFile?: (file?: File) => void
    onError?: (message: string) => void
    // Multi-image collection props
    imageCollection?: File[]
    currentImageIndex?: number
    onAddMore?: (file: File) => void | (() => void)  // For file: adds file, for camera: navigates to camera
    onNavigateToImage?: (index: number) => void
    onRemoveFromCollection?: (index: number) => void
}

export default function PictureScreen({
    pictureFile, 
    onBackToHome, 
    onOCRPicture, 
    onOCRCollection,
    onNavigateToHome, 
    source, 
    onRetake, 
    onChangeFile, 
    onError,
    imageCollection = [],
    currentImageIndex = 0,
    onAddMore,
    onNavigateToImage,
    onRemoveFromCollection
}: PictureScreenProps) {
    const [backgroundColor] = useState("#FAF9F7")
    const [textColor] = useState("#4E5562")
    const [fontFamily] = useState("Verdana, sans-serif")
    const [fontSize] = useState(18)
    const [lineSpacing] = useState(1.5)
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    /*
     * Held in a ref so the preview effect below can report a failure without
     * listing onError as a dependency. The parent passes a plain arrow, so its
     * identity changes on every render; depending on it would re-run the effect
     * each time, recreating the object URL and re-running the quality check on
     * exactly the low-memory devices this app keeps failing on.
     */
    const onErrorRef = useRef(onError);
    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const addMoreFileInputRef = useRef<HTMLInputElement>(null);
    
    // Multi-image collection helpers
    const hasMultipleImages = imageCollection.length > 1;
    const collectionCount = imageCollection.length;

    // Updated state
    const [imageSrc, setImageSrc] = useState<string>("");          // displayed/rotated image
    const [originalImageSrc, setOriginalImageSrc] = useState<string | null>(null); // original untouched image
    const [rotation, setRotation] = useState(0); // 0, 90, 180, 270
    const [isProcessing, setIsProcessing] = useState(false); // Loading state for OCR button
    
    // Image quality check state
    const [qualityResult, setQualityResult] = useState<QualityResult | null>(null);
    const [qualityWarningsDismissed, setQualityWarningsDismissed] = useState(false);

// Initialize both imageSrc and originalImageSrc when pictureFile changes
    useEffect(() => {
        async function loadImage() {
            if (!pictureFile) return;
            const imageUrl = URL.createObjectURL(pictureFile);
            setOriginalImageSrc(imageUrl);  // store untouched original
            setImageSrc(imageUrl);          // initial preview
            setRotation(0);            // reset rotation
            
            // Reset quality warnings when image changes
            setQualityResult(null);
            setQualityWarningsDismissed(false);

            /*
             * Guarded separately from the quality check below. loadImageToCanvas
             * takes a 2D context, and WebKit hands back null there once the page
             * passes its canvas memory budget — see DYS-008 — so on the phones this
             * app is actually failing on, this is a live throw, not a hypothetical.
             * The preview still renders from the object URL without a canvas; only
             * the pre-OCR transforms need one, and they re-derive it themselves.
             */
            try {
                const {canvas} = await loadImageToCanvas(imageUrl);
                canvasRef.current = canvas;
            } catch (error) {
                canvasRef.current = null;
                logger.warn("Could not prepare the image canvas, continuing with the preview only", {
                    error: error instanceof Error ? error.message : "Unknown error",
                });
            }
            
            // Run quality check (non-blocking)
            try {
                const quality = await analyzeImageQuality(pictureFile);
                setQualityResult(quality);
            } catch (error) {
                logger.warn("Quality check failed", { error: error instanceof Error ? error.message : 'Unknown error' });
                setQualityResult(null); // Graceful fallback - proceed without quality check
            }
        }

        /*
         * loadImage was called bare. Anything it threw became an unhandled
         * rejection, which reaches the logger via the listener DYS-003 installed
         * but shows the user nothing.
         */
        loadImage().catch((error) => {
            logger.error("Image preview failed to initialise", {
                error: error instanceof Error ? error : String(error),
            });
            onErrorRef.current?.("Nie udało się otworzyć tego zdjęcia. Spróbuj wybrać inny plik.");
        });
    }, [pictureFile]);

// Rotate left button handler
    const handleRotate = async () => {
        const newRotation = (rotation - 90 + 360) % 360;
        setRotation(newRotation);

        // always rotate from the original image, never from the preview
        if (originalImageSrc) {
            const rotatedImage = await rotateImage(originalImageSrc, newRotation);
            setImageSrc(rotatedImage);
        }
    };

/*
 * Always settles, and never rejects. It already degraded to the unrotated source
 * when the 2D context came back null, so every other failure takes the same exit:
 * the rotation becomes a visual no-op instead of a hang. Previously there was no
 * onerror handler and no guard around the canvas work, so an image that failed to
 * load left this promise pending forever and handleRotate awaited it forever —
 * the rotate button silently stopped responding for the rest of the session.
 */
    /**
     * The server resizes to 2000 px on arrival, so anything larger is bytes
     * uploaded to be thrown away - and on a phone, those bytes are the difference
     * between a page that reads and a 4 MB rejection.
     */
    const SERVER_RESIZE_MAX_EDGE = 2000;

    async function fitPictureForUpload(src: string): Promise<string> {
        const { canvas } = await loadImageToCanvas(src);
        const fitted = fitWithinMaxEdge(canvas, SERVER_RESIZE_MAX_EDGE);
        // An unchanged canvas means it already fits: return the original bytes
        // rather than re-encoding a photo that needed nothing.
        if (fitted === canvas) return src;
        return fitted.toDataURL(REENCODE_MIME, REENCODE_JPEG_QUALITY);
    }

    function rotateImage(imageSrc: string, angle: number) {
        return new Promise<string>((resolve) => {
            const img = new window.Image();
            img.onload = () => {
                try {
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        resolve(imageSrc);
                        return;
                    }

                    if (angle === 90 || angle === 270) {
                        // noinspection JSSuspiciousNameCombination
                        canvas.width = img.height;
                        // noinspection JSSuspiciousNameCombination
                        canvas.height = img.width;
                    } else {
                        canvas.width = img.width;
                        canvas.height = img.height;
                    }

                    ctx.translate(canvas.width / 2, canvas.height / 2);
                    ctx.rotate((angle * Math.PI) / 180);
                    ctx.drawImage(img, -img.width / 2, -img.height / 2);

                    /*
                     * JPEG. This runs at the source image's full resolution and
                     * whatever it returns is what gets uploaded, so a rotated
                     * twelve-megapixel gallery photo used to leave here as a
                     * lossless PNG several times the size of the JPEG it came
                     * from - straightening a picture was enough to push a working
                     * upload over the 4 MB ceiling. DYS-042.
                     */
                    resolve(canvas.toDataURL(REENCODE_MIME, REENCODE_JPEG_QUALITY));
                } catch (error) {
                    logger.warn("Rotation failed, keeping the current image", {
                        angle,
                        error: error instanceof Error ? error.message : "Unknown error",
                    });
                    resolve(imageSrc);
                }
            };
            img.onerror = () => {
                logger.warn("Could not load the image to rotate it", { angle });
                resolve(imageSrc);
            };
            img.src = imageSrc;
        });
    }

    // OCR button handler
    const handleOCRPicture2 = async () => {
        if (originalImageSrc != undefined) {
            //     const rotatedImage = await rotateImage(originalImageSrc, rotation);
            const rotatedImageFile = await convertUrlToFile(imageSrc, "test");
            await onOCRPicture(rotatedImageFile, rotation);  // pass rotated image to OCR
        }
    };

    const handleOCRPicture = async (): Promise<void> => {
        if (!originalImageSrc) return;

        return new Promise((resolve, reject) => {
            const img = new window.Image();
            img.onload = async () => {
                try {
                    const width = img.width;
                    const height = img.height;
                    logger.debug("Image dimensions for OCR", { width, height });
                    setJobContext({ imageWidth: width, imageHeight: height });

                    /*
                     * The photo is uploaded as taken. DYS-013.
                     *
                     * This used to downscale 50%, boost contrast, binarize at
                     * threshold 150 and sharpen, before the server did its own
                     * resize. Binarization is a Tesseract-era technique - it helps
                     * an engine that thresholds anyway - and a vision model is the
                     * opposite case: it reads a soft grayscale photo better than a
                     * hard black-and-white bitmap.
                     *
                     * Measured over five photographs, four runs each, driving the
                     * real transform through a browser (see
                     * scripts/ocr-bench/apply-client-pipeline.mjs). CER got worse on
                     * both pages that had ground truth, 0.002 -> 0.017 and
                     * 0.055 -> 0.063. And on a book page photographed with a shadow
                     * across it, the threshold turned a soft grey gradient into a
                     * solid black wedge over two thirds of the page: 955 characters
                     * read from the original, 480 from the processed copy, the rest
                     * erased rather than degraded. An unevenly lit phone photo is
                     * this app's primary input, so the step damaged the case it
                     * existed to serve.
                     *
                     * Not the reason originally given for removing it. The argument
                     * was that a threshold destroys Polish diacritics, and that
                     * turned out to be false - diacritic accuracy rose, 0.990 to
                     * 1.000 and 0.966 to 0.983.
                     *
                     * The server still resizes to 2000 px with sharp, which is
                     * where any preprocessing belongs: one place, measurable, and
                     * not on a phone's main thread.
                     *
                     * What stays is a plain downscale, and it is here for transport
                     * rather than for quality. The deleted step was quietly doing
                     * two jobs: it destroyed the page, and it also kept the upload
                     * under MAX_UPLOAD_BYTES. Removing it outright fixed the first
                     * and broke the second - two of the five photographs this was
                     * measured on are over 4 MB as taken, including the one that
                     * reads at CER 0.002, and they would have started coming back
                     * as "picture too large" instead of as text.
                     */
                    const fittedSrc = await fitPictureForUpload(imageSrc);
                    const rotatedImageFile = await convertUrlToFile(fittedSrc, "test");

                    /*
                     * The image path had no size check of any kind. An oversized
                     * upload reached the platform, came back 413 before our route
                     * ran, and arrived at the user as text that could not be
                     * recognised - a description of a picture that was never read.
                     * Same ceiling and same helper as the one-page PDF path, so the
                     * two cannot drift apart. DYS-042.
                     */
                    if (!isWithinUploadLimit(rotatedImageFile.size)) {
                        logger.warn("Image is over the upload limit", {
                            fileSize: rotatedImageFile.size,
                            limit: MAX_UPLOAD_BYTES,
                            width,
                            height,
                        });
                        onError?.(
                            `To zdjęcie jest za duże, aby je przesłać do rozpoznania `
                            + `(${formatMegabytes(rotatedImageFile.size)}, limit `
                            + `${formatMegabytes(MAX_UPLOAD_BYTES)}). Zrób zdjęcie w niższej `
                            + `rozdzielczości albo wybierz mniejszy plik.`
                        );
                        resolve();
                        return;
                    }

                    await onOCRPicture(rotatedImageFile, rotation); // Pass to OCR
                    resolve();
                } catch (error) {
                    reject(error);
                }
            };

            img.onerror = () => {
                logger.error("Failed to load image for resolution check");
                reject(new Error("Failed to load image for resolution check"));
            };

            img.src = imageSrc;
        });
    };

    /*
     * The second copy of the export button's bug, found by grepping the bundle
     * after DYS-027 shipped. This one was worse: it removed the anchor *and*
     * revoked the URL in the same tick as the click, so on iOS the download had
     * neither the element nor the blob by the time WebKit went looking for them.
     * The failure was invisible too - a console.error the user never sees and no
     * bug report ever carries.
     *
     * Now the same helper as the text export, so a phone gets the share sheet, a
     * Chrome or Edge desktop gets the OS save dialog (DYS-043), and everything
     * else gets an anchor that outlives its own click.
     */
    async function downloadImageAsBlob(imageUrl: string, filename: string = "obraz.jpg") {
        try {
            const response = await fetch(imageUrl, {mode: "cors"});
            const blob = await response.blob();

            const outcome = await saveFileToDevice(blob, filename, blob.type || 'image/jpeg');

            if (outcome === 'cancelled') return;

            /*
             * DYS-043 added a fourth outcome, 'saved', for the desktop save
             * dialog, and this toast deliberately keeps sharing a line with
             * 'downloaded'. Unlike the text export's toast it names no folder,
             * so it is equally true whether the user chose the location or the
             * browser did. Only the share sheet needs its own sentence, because
             * there the image went to another app rather than onto disk.
             */
            toast.success(outcome === 'shared'
                ? `Obraz ${filename} został udostępniony`
                : `Obraz zapisany jako ${filename}`);
        } catch (error) {
            logger.error('Image download failed', {
                error: error instanceof Error ? error : String(error),
                fileName: filename,
            });
            toast.error('Nie udało się zapisać obrazu. Spróbuj ponownie.');
        }
    }

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFileSelection(files[0]);
        }
        // Reset input value so the same file can be selected again
        e.target.value = '';
    };

    const handleFileSelection = (file: File) => {
        if (file.size > 10 * 1024 * 1024) {
            onError?.("File size too large. Please upload a file smaller than 10MB.");
            return;
        }

        const isImage = file.type.includes("image");
        const isPdf = file.type.includes("pdf");
        const isDocx =
            file.name.toLowerCase().endsWith(".docx") ||
            file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

        if (!isImage && !isPdf && !isDocx) {
            onError?.("Unsupported file format. Please upload an image, PDF, or Word (.docx) file.");
            return;
        }

        onChangeFile?.(file);
    };

    const handleChangeFileClick = () => {
        fileInputRef.current?.click();
    };

    // Handler for adding more files to collection
    const handleAddMoreFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.size > 10 * 1024 * 1024) {
                onError?.("File size too large. Please upload a file smaller than 10MB.");
                return;
            }
            if (!file.type.includes("image")) {
                onError?.("Unsupported file format. Please upload an image file.");
                return;
            }
            onAddMore?.(file);
        }
        // Reset input value so the same file can be selected again
        e.target.value = '';
    };

    const handleAddMoreClick = () => {
        if (source === "camera") {
            // For camera source, navigate back to camera to take another photo
            // The parent's onAddMore callback handles navigation
            (onAddMore as () => void)?.();
        } else {
            // For file source, open file picker
            addMoreFileInputRef.current?.click();
        }
    };

    // Handler for OCR - uses collection handler if multiple images, otherwise single image
    const handleOCRClick = async () => {
        // Immediately show loading state
        setIsProcessing(true);
        logger.info("OCR button clicked", { hasMultipleImages, collectionCount, source });
        try {
            if (hasMultipleImages && onOCRCollection) {
                await onOCRCollection();
            } else {
                await handleOCRPicture();
            }
        } catch (error) {
            /*
             * handleOCRPicture rejects on a failed transform or an image that will
             * not load, and there used to be no catch here. The finally below did
             * clear the spinner, so the button came back looking ready and nothing
             * else happened at all — indistinguishable, to the user, from a button
             * that does not work. Next 15 does not route a rejected promise to the
             * error boundary either, so nothing downstream picked it up.
             */
            const message = error instanceof Error ? error.message : "Unknown error";
            logger.error("OCR could not be started", { error: error instanceof Error ? error : message, source });
            onError?.("Nie udało się przygotować zdjęcia do rozpoznania. Spróbuj zrobić je ponownie lub wybrać inny plik.");
        } finally {
            // Reset loading state (though navigation will likely happen)
            setIsProcessing(false);
        }
    };

    // Navigation within collection
    const goToPrevImage = () => {
        if (currentImageIndex > 0) {
            onNavigateToImage?.(currentImageIndex - 1);
        }
    };

    const goToNextImage = () => {
        if (currentImageIndex < collectionCount - 1) {
            onNavigateToImage?.(currentImageIndex + 1);
        }
    };

    return (
        <div className="h-screen flex flex-col overflow-auto md:overflow-hidden" style={{backgroundColor: backgroundColor}}>
            <LogoHeader 
                onLogoClick={onNavigateToHome}
                onBackClick={onBackToHome}
                showBackButton={true}
                title="Zdjęcie"
            />
            <div className="flex-1 overflow-auto md:overflow-hidden pt-0 pb-0 sm:pb-0 sm:pt-0">
                <div className="h-full w-full max-w-4xl mx-auto px-4 pt-0 pb-0 md:pt-0 md:pb-4">
                    <Card className="w-full h-full flex flex-col border-none rounded-lg overflow-visible md:overflow-hidden shadow-md">
                        <TooltipProvider>
                        <CardContent
                            className="pt-4 pb-4 px-4 sm:pt-4 sm:pb-8 sm:px-8 text-center flex-1 flex flex-col min-h-0 relative overflow-visible md:overflow-hidden max-md:pb-0"
                            style={{
                                color: textColor,
                                fontSize: `${fontSize}px`,
                                lineHeight: lineSpacing,
                                fontFamily: fontFamily,
                            }}
                        >
                            {/* Quality warning banner - shows above image if issues detected */}
                            <QualityWarningBanner
                                qualityResult={qualityResult}
                                onDismiss={() => setQualityWarningsDismissed(true)}
                                dismissed={qualityWarningsDismissed}
                            />
                            
                            {/* Image container - takes most of the space */}
                            <div className="flex-1 relative min-h-0 overflow-hidden max-md:pb-28">
                                {imageSrc && (
                                    <img
                                        src={imageSrc}
                                        alt="Preview"
                                        className="absolute inset-0 w-full h-full object-contain object-top"
                                    />
                                )}
                                {/* Delete button - only show when multiple images */}
                                {hasMultipleImages && onRemoveFromCollection && (
                                    <button
                                        onClick={() => onRemoveFromCollection(currentImageIndex)}
                                        className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors z-10"
                                        aria-label="Usuń zdjęcie"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>

                            {/* Desktop: Bottom toolbar with action buttons */}
                            <div className="hidden md:flex flex-col items-center mt-4 flex-shrink-0 gap-3">
                                {/* Collection navigation - only show when multiple images */}
                                {hasMultipleImages && (
                                    <div className="flex items-center gap-3 mb-2">
                                        {/* Circular prev button */}
                                        <button
                                            onClick={goToPrevImage}
                                            disabled={currentImageIndex === 0}
                                            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#9CA3AF] text-white hover:bg-[#6B7280] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <ChevronLeft className="h-5 w-5" />
                                        </button>
                                        
                        {/* Page indicator pill */}
                        <span className="px-4 py-1.5 rounded-full bg-[#9CA3AF] text-white text-sm font-medium">
                            Strona {currentImageIndex + 1} z {collectionCount}
                        </span>
                                        
                                        {/* Circular next button */}
                                        <button
                                            onClick={goToNextImage}
                                            disabled={currentImageIndex === collectionCount - 1}
                                            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#9CA3AF] text-white hover:bg-[#6B7280] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <ChevronRight className="h-5 w-5" />
                                        </button>
                                    </div>
                                )}

                                {/* Action buttons row with labels */}
                                <div className="flex justify-center items-end gap-8">
                                    {/* Retake / Change file button - FIRST */}
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                onClick={source === "camera" ? onRetake : handleChangeFileClick}
                                                className="flex flex-col items-center gap-1 text-[#55366d] hover:text-[#55366d]/70 transition-colors"
                                            >
                                                {source === "camera" ? (
                                                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M22 8C22 7.46957 21.7893 6.96086 21.4142 6.58579C21.0391 6.21071 20.5304 6 20 6H18.003C17.6422 6.00001 17.2881 5.90241 16.9783 5.71756C16.6684 5.53271 16.4144 5.26749 16.243 4.95L15.757 4.05C15.5856 3.73251 15.3316 3.46729 15.0217 3.28244C14.7119 3.09759 14.3578 2.99999 13.997 3H10.004C9.64358 2.99998 9.28985 3.09736 8.98022 3.28183C8.67059 3.46631 8.41656 3.73103 8.245 4.048L7.756 4.952C7.58444 5.26897 7.33041 5.53369 7.02078 5.71817C6.71115 5.90264 6.35742 6.00002 5.997 6H4C3.46957 6 2.96086 6.21071 2.58579 6.58579C2.21071 6.96086 2 7.46957 2 8V17C2 17.5304 2.21071 18.0391 2.58579 18.4142C2.96086 18.7893 3.46957 19 4 19H8" />
                                                        <path d="M9.87868 14.1218C8.70711 12.9502 8.70711 11.0507 9.87868 9.87913C11.0503 8.70756 12.9497 8.70756 14.1213 9.87913" />
                                                        <path d="M19 17L22 14L19 11" />
                                                        <path d="M22 21L17.5 21C16.5717 21 15.6815 20.6312 15.0251 19.9749C14.3687 19.3185 14 18.4283 14 17.5C14 16.5717 14.3687 15.6815 15.0251 15.0251C15.6815 14.3687 16.5717 14 17.5 14L22 14" />
                                                    </svg>
                                                ) : (
                                                    <FolderOpen className="h-5 w-5" />
                                                )}
                                                <span className="text-[11px]">
                                                    {source === "camera" ? "Ponów" : "Zmień plik"}
                                                </span>
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{source === "camera" ? "Zrób nowe zdjęcie" : "Wybierz inny plik"}</p>
                                        </TooltipContent>
                                    </Tooltip>

                                    {/* Add more button */}
                                    {onAddMore && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    onClick={handleAddMoreClick}
                                                    className="flex flex-col items-center gap-1 text-[#55366d] hover:text-[#55366d]/70 transition-colors"
                                                >
                                                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M16 5H22" />
                                                        <path d="M19 2V8" />
                                                        <path d="M21 11.5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H12.5" />
                                                        <path d="M21 14.9997L17.914 11.9137C17.5389 11.5388 17.0303 11.3281 16.5 11.3281C15.9697 11.3281 15.4611 11.5388 15.086 11.9137L6 20.9997" />
                                                        <path d="M9 11C10.1046 11 11 10.1046 11 9C11 7.89543 10.1046 7 9 7C7.89543 7 7 7.89543 7 9C7 10.1046 7.89543 11 9 11Z" />
                                                    </svg>
                                                    <span className="text-[11px]">Dodaj</span>
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Dodaj kolejne zdjęcie</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}

                                    {/* Rotate button */}
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                onClick={handleRotate}
                                                className="flex flex-col items-center gap-1 text-[#55366d] hover:text-[#55366d]/70 transition-colors"
                                            >
                                                <RotateCcw className="h-5 w-5" />
                                                <span className="text-[11px]">Obróć</span>
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Obróć obraz w lewo</p>
                                        </TooltipContent>
                                    </Tooltip>

                                    {/* Save button - only show for camera source */}
                                    {source === "camera" && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    onClick={() => {
                                                        if (imageSrc) {
                                                            downloadImageAsBlob(imageSrc, "obraz.jpg");
                                                        }
                                                    }}
                                                    className="flex flex-col items-center gap-1 text-[#55366d] hover:text-[#55366d]/70 transition-colors"
                                                >
                                                    <Save className="h-5 w-5" />
                                                    <span className="text-[11px]">Zapisz</span>
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Zapisz zdjęcie na urządzenie</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                </div>

                                {/* Main OCR button */}
                                <Button
                                    onClick={handleOCRClick}
                                    className="bg-[rgba(85,54,109,1)] hover:bg-[rgba(85,54,109,0.9)] rounded-full text-white px-6 disabled:opacity-70"
                                    size="default"
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? (
                                        <>
                                            <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block"></span>
                                            Przetwarzanie...
                                        </>
                                    ) : (
                                        <>Rozpoznaj tekst{hasMultipleImages ? ` (${collectionCount})` : ''}</>
                                    )}
                                </Button>
                            </div>

                        </CardContent>
                        </TooltipProvider>
                    </Card>
                </div>
            </div>

            {/* Hidden file input for changing files */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileInputChange}
                className="hidden"
                aria-label="Change file"
            />

            {/* Hidden file input for adding more files to collection */}
            <input
                ref={addMoreFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAddMoreFileInputChange}
                className="hidden"
                aria-label="Add more files"
            />

            {/* Mobile: Fixed bottom toolbar */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm px-4 py-3 flex flex-col items-center gap-2 z-50">
                {/* Collection navigation - only show when multiple images */}
                {hasMultipleImages && (
                    <div className="flex items-center gap-3 mb-1">
                        {/* Circular prev button */}
                        <button
                            onClick={goToPrevImage}
                            disabled={currentImageIndex === 0}
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#9CA3AF] text-white active:bg-[#6B7280] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        
                        {/* Page indicator pill */}
                        <span className="px-4 py-1.5 rounded-full bg-[#9CA3AF] text-white text-sm font-medium">
                            Strona {currentImageIndex + 1} z {collectionCount}
                        </span>
                        
                        {/* Circular next button */}
                        <button
                            onClick={goToNextImage}
                            disabled={currentImageIndex === collectionCount - 1}
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#9CA3AF] text-white active:bg-[#6B7280] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    </div>
                )}

                {/* Action buttons row with labels */}
                <div className="flex justify-center items-end gap-8">
                    {/* Retake / Change file button - FIRST */}
                    <button
                        onClick={source === "camera" ? onRetake : handleChangeFileClick}
                        className="flex flex-col items-center gap-0.5 text-[#55366d] active:text-[#55366d]/70"
                    >
                        {source === "camera" ? (
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 8C22 7.46957 21.7893 6.96086 21.4142 6.58579C21.0391 6.21071 20.5304 6 20 6H18.003C17.6422 6.00001 17.2881 5.90241 16.9783 5.71756C16.6684 5.53271 16.4144 5.26749 16.243 4.95L15.757 4.05C15.5856 3.73251 15.3316 3.46729 15.0217 3.28244C14.7119 3.09759 14.3578 2.99999 13.997 3H10.004C9.64358 2.99998 9.28985 3.09736 8.98022 3.28183C8.67059 3.46631 8.41656 3.73103 8.245 4.048L7.756 4.952C7.58444 5.26897 7.33041 5.53369 7.02078 5.71817C6.71115 5.90264 6.35742 6.00002 5.997 6H4C3.46957 6 2.96086 6.21071 2.58579 6.58579C2.21071 6.96086 2 7.46957 2 8V17C2 17.5304 2.21071 18.0391 2.58579 18.4142C2.96086 18.7893 3.46957 19 4 19H8" />
                                <path d="M9.87868 14.1218C8.70711 12.9502 8.70711 11.0507 9.87868 9.87913C11.0503 8.70756 12.9497 8.70756 14.1213 9.87913" />
                                <path d="M19 17L22 14L19 11" />
                                <path d="M22 21L17.5 21C16.5717 21 15.6815 20.6312 15.0251 19.9749C14.3687 19.3185 14 18.4283 14 17.5C14 16.5717 14.3687 15.6815 15.0251 15.0251C15.6815 14.3687 16.5717 14 17.5 14L22 14" />
                            </svg>
                        ) : (
                            <FolderOpen className="h-5 w-5" />
                        )}
                        <span className="text-[10px]">
                            {source === "camera" ? "Ponów" : "Zmień plik"}
                        </span>
                    </button>

                    {/* Add more button */}
                    {onAddMore && (
                        <button
                            onClick={handleAddMoreClick}
                            className="flex flex-col items-center gap-0.5 text-[#55366d] active:text-[#55366d]/70"
                        >
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 5H22" />
                                <path d="M19 2V8" />
                                <path d="M21 11.5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H12.5" />
                                <path d="M21 14.9997L17.914 11.9137C17.5389 11.5388 17.0303 11.3281 16.5 11.3281C15.9697 11.3281 15.4611 11.5388 15.086 11.9137L6 20.9997" />
                                <path d="M9 11C10.1046 11 11 10.1046 11 9C11 7.89543 10.1046 7 9 7C7.89543 7 7 7.89543 7 9C7 10.1046 7.89543 11 9 11Z" />
                            </svg>
                            <span className="text-[10px]">Dodaj</span>
                        </button>
                    )}

                    {/* Rotate button */}
                    <button
                        onClick={handleRotate}
                        className="flex flex-col items-center gap-0.5 text-[#55366d] active:text-[#55366d]/70"
                    >
                        <RotateCcw className="h-5 w-5" />
                        <span className="text-[10px]">Obróć</span>
                    </button>

                    {/* Save button - only show for camera source */}
                    {source === "camera" && (
                        <button
                            onClick={() => {
                                if (imageSrc) {
                                    downloadImageAsBlob(imageSrc, "obraz.jpg");
                                }
                            }}
                            className="flex flex-col items-center gap-0.5 text-[#55366d] active:text-[#55366d]/70"
                        >
                            <Save className="h-5 w-5" />
                            <span className="text-[10px]">Zapisz</span>
                        </button>
                    )}
                </div>

                {/* Main OCR button */}
                <Button
                    onClick={handleOCRClick}
                    className="bg-[rgba(85,54,109,1)] hover:bg-[rgba(85,54,109,0.9)] rounded-full text-white shadow-md px-6 disabled:opacity-70"
                    size="default"
                    disabled={isProcessing}
                >
                    {isProcessing ? (
                        <>
                            <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block"></span>
                            Przetwarzanie...
                        </>
                    ) : (
                        <>Rozpoznaj tekst{hasMultipleImages ? ` (${collectionCount})` : ''}</>
                    )}
                </Button>
            </div>
        </div>
    )
}
